import { useCallback, useMemo, useState } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { apiFetch, getErrorMessage } from '@/lib/api'
import type { PersonRole, ReferencePhoto } from '@cugumi/shared/types'
import { MAX_FILE_SIZE, ALLOWED_FILE_TYPES } from '@cugumi/shared/types'
import { useUIStore } from '@/stores/uiStore'

export type { ReferencePhoto } from '@cugumi/shared/types'

const EMPTY_PHOTOS: ReferencePhoto[] = []

interface UseReferencePhotosReturn {
  photos: ReferencePhoto[]
  groomPhotos: ReferencePhoto[]
  bridePhotos: ReferencePhoto[]
  isLoading: boolean
  uploading: PersonRole | null
  upload: (file: File, role: PersonRole) => Promise<void>
  remove: (id: string) => Promise<void>
  getByRole: (role: PersonRole) => ReferencePhoto[]
  refetch: () => void
}

export function useReferencePhotos(): UseReferencePhotosReturn {
  const { data: photos, isLoading, refetch } = useApiFetch<ReferencePhoto[]>('/api/ai/reference-photos')
  const addToast = useUIStore((s) => s.addToast)
  const [uploading, setUploading] = useState<PersonRole | null>(null)

  const upload = useCallback(async (file: File, role: PersonRole) => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      addToast({ type: 'error', message: 'JPG, PNG, WebP 파일만 업로드 가능합니다' })
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      addToast({ type: 'error', message: '파일 크기는 10MB 이하만 가능합니다' })
      return
    }

    setUploading(role)
    try {
      const formData = new FormData()
      formData.append('image', file)
      formData.append('role', role)

      const result = await apiFetch<ReferencePhoto>(
        '/api/ai/reference-photos',
        { method: 'POST', body: formData },
      )

      if (!result.success) {
        throw new Error(getErrorMessage(result.error, '업로드에 실패했습니다'))
      }

      await refetch()
    } catch (err) {
      throw err instanceof Error ? err : new Error('사진 업로드에 실패했습니다')
    } finally {
      setUploading(null)
    }
  }, [refetch, addToast])

  const remove = useCallback(async (id: string) => {
    try {
      const result = await apiFetch(`/api/ai/reference-photos/${id}`, { method: 'DELETE' })
      if (!result.success) {
        addToast({ type: 'error', message: getErrorMessage(result.error, '삭제에 실패했습니다') })
        return
      }
      await refetch()
    } catch {
      addToast({ type: 'error', message: '사진 삭제에 실패했습니다' })
    }
  }, [refetch, addToast])

  const allPhotos = photos ?? EMPTY_PHOTOS
  const groomPhotos = useMemo(() => allPhotos.filter((p) => p.role === 'GROOM'), [allPhotos])
  const bridePhotos = useMemo(() => allPhotos.filter((p) => p.role === 'BRIDE'), [allPhotos])

  const getByRole = useCallback(
    (role: PersonRole) => allPhotos.filter((p) => p.role === role),
    [allPhotos],
  )

  return { photos: allPhotos, groomPhotos, bridePhotos, isLoading, uploading, upload, remove, getByRole, refetch }
}
